// WebXR / immersive-vr session manager.
//
// Why an inline VR button (rendered into the body):
//   navigator.xr.requestSession() requires transient user activation. That activation
//   does not survive cross-frame postMessage hops — so the click handler that calls
//   app.xr.start() must live inside the same document as the canvas. We're not in an
//   iframe here, so the body-level button works directly.
//
// Inside VR, HTML overlays do NOT render — only the WebGL scene. So gaze reticle and
// hotspot dots are PlayCanvas entities (not DOM). HTML modals/labels are skipped while
// active.

import * as pc from 'playcanvas';
import { Emitter } from '../app/emitter';
import type { Hotspot } from '../app/types';
import type { HotspotManager } from './hotspotManager';
import { VR_GAZE_DWELL_MS, VR_REFERENCE_SPACE } from '../app/config';

export type XrSessionEvent =
  | { type: 'supported' }
  | { type: 'unsupported'; reason?: string }
  | { type: 'enter' }
  | { type: 'exit' }
  // Controller grip (squeeze) held/released — drives push-to-talk voice nav.
  // The trigger (`select`) stays bound to hotspot ray-pick so the two don't
  // conflict. Coalesced across both controllers: 'squeeze-start' fires on the
  // first grip down, 'squeeze-end' when the last grip is released.
  | { type: 'squeeze-start' }
  | { type: 'squeeze-end' }
  | { type: 'error'; error: Error };

export class XrManager {
  private readonly app: pc.Application;
  private readonly camera: pc.Entity;
  private readonly hotspots: HotspotManager;
  private readonly emitter = new Emitter<XrSessionEvent>();

  private supported = false;
  private active = false;
  private reticle: pc.Entity | null = null;
  private reticleMaterial: pc.StandardMaterial | null = null;
  private gazeStartMs = 0;
  private gazeTarget: Hotspot | null = null;
  /** Count of controllers currently squeezing — coalesces the two grips into
   *  a single push-to-talk start/end pair. */
  private squeezeCount = 0;
  /** True while push-to-talk recording — freezes the reticle big+red so the
   *  gaze tick doesn't shrink it back. */
  private voiceRecording = false;

  private readonly rayOrigin = new pc.Vec3();
  private readonly rayDir = new pc.Vec3();
  private readonly camForward = new pc.Vec3();

  constructor(app: pc.Application, camera: pc.Entity, hotspots: HotspotManager) {
    this.app = app;
    this.camera = camera;
    this.hotspots = hotspots;

    this.detectSupport();
    this.bindAppEvents();
    this.bindInput();
    this.app.on('update', this.tickGaze);
  }

  on(listener: (e: XrSessionEvent) => void): () => void {
    return this.emitter.on(listener);
  }

  isSupported(): boolean {
    return this.supported;
  }

  isActive(): boolean {
    return this.active;
  }

  enter(): boolean {
    if (!this.supported || this.active) return false;
    if (!this.camera.camera) return false;
    if (!this.app.xr) return false;
    const space = VR_REFERENCE_SPACE === 'local-floor' ? pc.XRSPACE_LOCALFLOOR : pc.XRSPACE_LOCAL;
    this.app.xr.start(this.camera.camera, pc.XRTYPE_VR, space, {
      callback: (err) => {
        if (err) {
          this.emitter.emit({ type: 'error', error: err as unknown as Error });
        }
      },
    });
    return true;
  }

  exit(): void {
    if (this.active && this.app.xr) this.app.xr.end();
  }

  destroy(): void {
    this.app.off('update', this.tickGaze);
    this.destroyReticle();
  }

  // ── Internals ─────────────────────────────────────────────────

  private detectSupport(): void {
    const xr = (navigator as unknown as { xr?: NavigatorXr }).xr;
    if (!xr || typeof xr.isSessionSupported !== 'function') {
      this.supported = false;
      this.emitter.emit({ type: 'unsupported', reason: 'navigator.xr not available' });
      return;
    }
    xr.isSessionSupported('immersive-vr')
      .then((s: boolean) => {
        this.supported = s;
        this.emitter.emit({ type: s ? 'supported' : 'unsupported' });
      })
      .catch((err: unknown) => {
        this.supported = false;
        this.emitter.emit({ type: 'unsupported', reason: String(err) });
      });
  }

  private bindAppEvents(): void {
    if (!this.app.xr) return;
    this.app.xr.on('start', () => {
      this.active = true;
      this.createReticle();
      this.emitter.emit({ type: 'enter' });
    });
    this.app.xr.on('end', () => {
      this.active = false;
      this.destroyReticle();
      this.gazeTarget = null;
      this.squeezeCount = 0;
      this.emitter.emit({ type: 'exit' });
    });
    this.app.xr.on('error', (err: unknown) => {
      this.emitter.emit({ type: 'error', error: err as Error });
    });
  }

  private bindInput(): void {
    const input = this.app.xr?.input;
    if (!input) return;
    input.on('add', (inputSource: pc.XrInputSource) => {
      // Trigger → ray-pick a hotspot.
      inputSource.on('select', () => this.onControllerSelect(inputSource));
      // Grip / squeeze → push-to-talk voice navigation (held while speaking).
      inputSource.on('squeezestart', () => this.onSqueezeStart());
      inputSource.on('squeezeend', () => this.onSqueezeEnd());
      // If a controller drops mid-squeeze, treat it as a release so we don't
      // leave the count stuck above zero.
      inputSource.on('remove', () => this.onSqueezeEnd());
    });
  }

  private onControllerSelect(input: pc.XrInputSource): void {
    if (!this.active) return;
    const origin = input.getOrigin();
    const dir = input.getDirection();
    this.rayOrigin.copy(origin);
    this.rayDir.copy(dir).normalize();
    const hit = this.hotspots.pickFromRay(this.rayOrigin, this.rayDir, 0.12);
    if (hit) this.hotspots.triggerSelect(hit, 'controller');
  }

  private onSqueezeStart(): void {
    if (!this.active) return;
    this.squeezeCount += 1;
    if (this.squeezeCount === 1) this.emitter.emit({ type: 'squeeze-start' });
  }

  private onSqueezeEnd(): void {
    if (this.squeezeCount === 0) return;
    this.squeezeCount -= 1;
    if (this.squeezeCount === 0) this.emitter.emit({ type: 'squeeze-end' });
  }

  private createReticle(): void {
    const e = new pc.Entity('vr-reticle');
    e.addComponent('render', { type: 'sphere' });
    e.setLocalScale(0.025, 0.025, 0.025);
    this.camera.addChild(e);
    e.setLocalPosition(0, 0, -1.4);

    const mat = new pc.StandardMaterial();
    mat.useLighting = false;
    mat.useFog = false;
    mat.useTonemap = false;
    mat.diffuse = new pc.Color(0, 0, 0);
    mat.emissive = new pc.Color(0.96, 0.77, 0.19);
    mat.opacity = 0.85;
    mat.blendType = pc.BLEND_NORMAL;
    mat.depthWrite = false;
    mat.depthTest = false; // always on top
    mat.update();

    const meshInstances = e.render?.meshInstances ?? [];
    for (const mi of meshInstances) {
      mi.material = mat;
    }

    this.reticle = e;
    this.reticleMaterial = mat;
  }

  private destroyReticle(): void {
    this.reticle?.destroy();
    this.reticleMaterial?.destroy();
    this.reticle = null;
    this.reticleMaterial = null;
  }

  /**
   * Signal push-to-talk state on the gaze reticle: while recording it grows
   * and turns red; idle it returns to the small gold dot. Visible feedback
   * complements the audio cue — the dot is bigger + red so it reads even
   * against bright panoramas (e.g. the white bathroom). While recording, the
   * gaze tick is suppressed (see tickGaze) so this size/colour sticks.
   */
  setVoiceIndicator(recording: boolean): void {
    this.voiceRecording = recording;
    if (!this.reticle || !this.reticleMaterial) return;
    if (recording) {
      this.reticleMaterial.emissive.set(0.95, 0.12, 0.12);
      this.reticle.setLocalScale(0.06, 0.06, 0.06);
    } else {
      this.reticleMaterial.emissive.set(0.96, 0.77, 0.19);
      this.reticle.setLocalScale(0.025, 0.025, 0.025);
    }
    this.reticleMaterial.update();
  }

  /**
   * Per-frame gaze tracking. Casts a ray from the camera forward; if the user holds
   * gaze on the same hotspot for VR_GAZE_DWELL_MS, fires it.
   */
  private readonly tickGaze = (_dt: number): void => {
    if (!this.active) return;
    // While the operator is holding grip-to-talk, freeze the reticle in its
    // big-red recording state — don't run gaze dwell or resize it.
    if (this.voiceRecording) return;
    // Camera forward in world space: rotate (0,0,-1) by camera world rotation.
    this.camera.forward; // accessor
    const fwd = this.camera.forward; // pc.Vec3, already a copy
    this.camForward.copy(fwd).normalize();
    this.rayOrigin.copy(this.camera.getPosition());
    this.rayDir.copy(this.camForward);

    const hit = this.hotspots.pickFromRay(this.rayOrigin, this.rayDir, 0.06);
    const now = performance.now();
    if (hit && hit.id === this.gazeTarget?.id) {
      if (now - this.gazeStartMs >= VR_GAZE_DWELL_MS) {
        const fired = hit;
        this.gazeTarget = null; // prevent immediate re-fire
        this.gazeStartMs = 0;
        this.hotspots.triggerSelect(fired, 'gaze');
      } else {
        this.updateReticleProgress((now - this.gazeStartMs) / VR_GAZE_DWELL_MS);
      }
    } else {
      this.gazeTarget = hit;
      this.gazeStartMs = hit ? now : 0;
      this.updateReticleProgress(0);
    }
  };

  private updateReticleProgress(p: number): void {
    if (!this.reticle) return;
    // Reticle inflates as the dwell timer fills.
    const scale = 0.025 + 0.025 * Math.min(1, Math.max(0, p));
    this.reticle.setLocalScale(scale, scale, scale);
  }
}

interface NavigatorXr {
  isSessionSupported(mode: 'immersive-vr' | 'immersive-ar' | 'inline'): Promise<boolean>;
}

